import React, { useState } from 'react';
import { X, MessageSquare, CheckCircle } from 'lucide-react';

const CATEGORIES = [
  { value: 'route', label: 'Route or Directions' },
  { value: 'stop', label: 'A Specific Stop' },
  { value: 'description', label: 'Walk Description' },
  { value: 'missing', label: 'Missing a Great Spot' },
  { value: 'other', label: 'Other' },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const WalkFeedbackModal = ({ walk, isOpen, onClose }) => {
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 20) { setError('Please write at least 20 characters.'); return; }
    if (!category) { setError('Please select a category.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${BACKEND}/api/walks/${walk.id}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walk_id: walk.id,
          walk_title: walk.title,
          category,
          message: message.trim(),
          email: email.trim() || null,
        }),
      });
      if (resp.ok) {
        setSubmitted(true);
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data.detail || 'Failed to submit. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCategory('');
    setMessage('');
    setEmail('');
    setSubmitted(false);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      <div className="relative bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>

        {/* Gradient header */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 px-6 pt-5 pb-4">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors"
          >
            <X className="h-4 w-4 text-white" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="h-5 w-5 text-white/80" />
            <span className="text-white/80 text-sm font-medium">Community Feedback</span>
          </div>
          <h2 className="text-xl font-bold text-white">{walk.title}</h2>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Thanks for your input!</h3>
            <p className="text-sm text-gray-600 mb-6">
              Your feedback helps make Wanderloop better for everyone.
            </p>
            <button
              onClick={handleClose}
              className="px-5 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Help us make this walk better. Local knowledge makes Wanderloop great.
            </p>
            <p className="text-[10px] text-gray-400 leading-relaxed">
              By submitting, you agree that your contribution may be used to improve this walk listing. Wanderloop reserves the right to edit or decline submissions.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What would you like to improve?
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-md border-gray-300 focus:ring-purple-500 focus:border-purple-500 text-sm"
                required
              >
                <option value="">Select a category...</option>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tell us what you know
              </label>
              <textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); setError(''); }}
                rows={4}
                className="w-full rounded-md border-gray-300 focus:ring-purple-500 focus:border-purple-500 text-sm"
                placeholder="What should we add, change, or fix about this walk?"
                required
                minLength={20}
              />
              <p className="text-xs text-gray-400 mt-0.5">{message.length}/20 minimum characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your email <span className="font-normal text-gray-400">(optional, if you'd like credit)</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border-gray-300 focus:ring-purple-500 focus:border-purple-500 text-sm"
                placeholder="you@example.com"
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={submitting || message.trim().length < 20 || !category}
              className="w-full py-2.5 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default WalkFeedbackModal;
